import { useState } from "react";
import { Loader2, Coffee, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import SponsorPlaceholder from "../components/SponsorPlaceholder";
import Testimonials from "../components/Testimonials";
import ScrollToTop from "../components/ScrollToTop";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dark theme color palette - extracted from ClauseClear logo
const colors = {
  background: '#2E334A',      // Deep muted navy (logo background)
  backgroundLight: '#3A3F54', // Slightly lighter for cards
  backgroundDark: '#252938',  // Darker sections
  text: '#FFFFFF',            // Pure white - primary text
  textMuted: '#B8BCC8',       // Muted grey - secondary text
  textDim: '#8A8F9E',         // Even more muted - tertiary text
  accent: '#D4AF37',          // Rich metallic gold (logo rings)
  accentHover: '#C9A432',     // Slightly darker gold for hover
  border: '#3E4459',          // Subtle border
  borderLight: '#4E536A',     // Lighter border for focus/hover
  info: '#3A4A6B',            // Info background
  infoBorder: '#4A5A7B',      // Info border
  error: '#5A3A3A',           // Error background
  errorText: '#FFB8B8',       // Error text
};

export default function Home() {
  const [clauseText, setClauseText] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleAnalyze = async () => {
    if (!clauseText.trim()) {
      setError("Please enter some text to analyze");
      return;
    }

    setLoading(true);
    setError("");
    setAnalysis(null);

    try {
      const response = await axios.post(`${API}/analyze`, {
        clause_text: clauseText,
      });
      setAnalysis(response.data);
      
      setTimeout(() => {
        document.getElementById('results')?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    } catch (err) {
      setError("Failed to analyze clause. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Hero Section */}
      <div className="relative px-6 md:px-12 lg:px-24 py-16 md:py-24">
        {/* Subtle decorative ring motif */}
        <div 
          className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-[0.03] pointer-events-none"
          style={{ 
            border: `1px solid ${colors.accent}`,
            transform: 'translate(30%, -30%)'
          }}
        />
        <div 
          className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-[0.02] pointer-events-none"
          style={{ 
            border: `1px solid ${colors.accent}`,
            transform: 'translate(-40%, 40%)'
          }}
        />

        <div className="max-w-4xl mx-auto relative">
          {/* Logo Header */}
          <div className="flex justify-center mb-12">
            <img 
              src="/clauseclear-logo.png" 
              alt="ClauseClear" 
              className="h-20 md:h-24 object-contain"
            />
          </div>

          <h1 
            className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] mb-8 text-center"
            data-testid="main-heading"
            style={{ color: colors.text }}
          >
            Understand contract clauses in plain English
          </h1>
          <p 
            className="text-lg md:text-xl leading-relaxed mb-16 text-center max-w-3xl mx-auto"
            data-testid="subtitle"
            style={{ color: colors.textMuted }}
          >
            ClauseClear explains confusing legal clauses, highlights common risks, and helps you understand what matters before you sign — without legal jargon or advice.
          </p>

          {/* Input Section */}
          <div 
            className="rounded-xl p-8 md:p-10 mb-20"
            style={{ 
              backgroundColor: colors.backgroundLight, 
              border: `1px solid ${colors.border}` 
            }}
          >
            <textarea
              data-testid="clause-input"
              value={clauseText}
              onChange={(e) => setClauseText(e.target.value)}
              placeholder="Paste a clause or section of a contract here..."
              className="w-full focus:outline-none rounded-lg p-6 text-base resize-none transition-all min-h-[200px]"
              style={{
                backgroundColor: colors.background,
                border: clauseText ? `2px solid ${colors.accent}` : `2px solid ${colors.border}`,
                fontFamily: "'Inter', sans-serif",
                color: colors.text,
              }}
            />
            
            <div className="mt-4 text-sm text-left" style={{ color: colors.textDim }}>
              <p className="mb-2">
                Paste any clause from an employment contract, freelance agreement, or service contract.
              </p>
              <p>
                This tool explains language and structure — it does not provide legal advice.
              </p>
            </div>

            {error && (
              <div 
                data-testid="error-message"
                className="mt-4 p-4 rounded-lg text-sm"
                style={{ backgroundColor: colors.error, color: colors.errorText }}
              >
                {error}
              </div>
            )}

            <div className="flex flex-col md:flex-row gap-4 mt-6">
              <button
                data-testid="analyze-button"
                onClick={handleAnalyze}
                disabled={loading}
                className="rounded-lg px-8 py-4 text-base font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  backgroundColor: loading ? colors.accentHover : colors.accent,
                  color: colors.background,
                }}
                onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = colors.accentHover)}
                onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = colors.accent)}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Analyzing...
                  </span>
                ) : (
                  'Analyze Clause'
                )}
              </button>
              <a
                href="https://buymeacoffee.com/jamesmoneyz"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 text-base font-medium transition-all px-8 py-4 rounded-lg"
                style={{ 
                  border: `1px solid ${colors.border}`, 
                  color: colors.textMuted, 
                  backgroundColor: 'transparent' 
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.backgroundLight;
                  e.currentTarget.style.borderColor = colors.borderLight;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = colors.border;
                }}
              >
                <Coffee className="w-5 h-5" />
                Support the project
              </a>
            </div>
          </div>

          {/* What ClauseClear Does */}
          <section className="mb-20">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-8 h-px" style={{ backgroundColor: colors.accent }} />
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight" style={{ color: colors.text }}>
                What ClauseClear Does
              </h2>
            </div>
            <p className="text-lg leading-relaxed mb-6" style={{ color: colors.textMuted }}>
              ClauseClear provides plain-English explanations of commonly used contract clauses, including:
            </p>
            <ul className="space-y-3 ml-6 mb-8">
              {['Employment contract clauses', 'Termination and notice provisions', 'Fixed-term and probation clauses', 'Non-compete and restraint clauses', 'Indemnity and liability clauses'].map((item, i) => (
                <li key={i} className="text-lg leading-relaxed flex items-start gap-3" style={{ color: colors.textMuted }}>
                  <span style={{ color: colors.accent }}>•</span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-lg leading-relaxed mb-5" style={{ color: colors.textMuted }}>
              Each explanation focuses on:
            </p>
            <ul className="space-y-3 ml-6">
              {['What the clause usually means', 'Why it exists', 'What people often misunderstand', 'When it deserves closer attention'].map((item, i) => (
                <li key={i} className="text-lg leading-relaxed flex items-start gap-3" style={{ color: colors.textMuted }}>
                  <span style={{ color: colors.accent }}>•</span>
                  {item}
                </li>
              ))}
            </ul>
          </section>

          {/* Who It's For */}
          <section className="mb-20">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-8 h-px" style={{ backgroundColor: colors.accent }} />
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight" style={{ color: colors.text }}>
                Who It's For
              </h2>
            </div>
            <p className="text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              ClauseClear is designed for everyday people, employees, founders, and SMEs — not lawyers. It's useful for anyone reviewing employment contracts, understanding freelance agreements, or making sense of service contracts without needing a law degree.
            </p>
          </section>

          {/* Important Notice */}
          <section className="mb-20">
            <div 
              className="p-8 rounded-xl"
              style={{ 
                backgroundColor: colors.info, 
                border: `1px solid ${colors.infoBorder}` 
              }}
            >
              <p className="text-base leading-relaxed" style={{ color: colors.text }}>
                <strong style={{ color: colors.accent }}>Important:</strong> ClauseClear is an educational tool designed to help people better understand contract language. It does not provide legal advice, legal opinions, or replace a qualified lawyer.
              </p>
            </div>
          </section>

          {/* Browse Contract Clauses */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-8 h-px" style={{ backgroundColor: colors.accent }} />
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight" style={{ color: colors.text }}>
                Browse Contract Clauses
              </h2>
            </div>
            <div className="mb-8">
              <Link
                to="/clauses"
                className="inline-flex items-center gap-2 text-lg font-medium transition-opacity hover:opacity-80"
                style={{ color: colors.accent }}
              >
                View all clause explanations
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { to: '/explain-non-compete-clause', title: 'Non-compete clause', desc: 'Understand work restrictions after employment' },
                { to: '/explain-notice-period-clause', title: 'Notice period clause', desc: 'Learn about resignation and termination timelines' },
                { to: '/explain-termination-for-cause-clause', title: 'Termination for cause', desc: 'Understand immediate dismissal provisions' },
                { to: '/explain-indemnity-clause', title: 'Indemnity clause', desc: 'Learn about liability protection requirements' },
              ].map((item, i) => (
                <Link 
                  key={i}
                  to={item.to}
                  className="p-6 rounded-lg transition-all"
                  style={{ 
                    backgroundColor: colors.backgroundLight, 
                    border: `1px solid ${colors.border}` 
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = colors.borderLight;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = colors.border;
                  }}
                >
                  <h3 className="font-medium text-lg mb-1" style={{ color: colors.accent }}>{item.title}</h3>
                  <p className="text-sm" style={{ color: colors.textDim }}>{item.desc}</p>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* Testimonials Section */}
      <Testimonials />

      {/* Sponsorship & Closing Section */}
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">
          <SponsorPlaceholder variant="disclosure" />
        </div>
      </div>

      {/* Results Section */}
      {analysis && (
        <div 
          id="results"
          data-testid="analysis-results"
          className="px-6 md:px-12 lg:px-24 py-16 animate-fade-in"
          style={{ backgroundColor: colors.backgroundDark, borderTop: `1px solid ${colors.border}` }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-12">
              <div className="w-12 h-px" style={{ backgroundColor: colors.accent }} />
              <h2 
                className="text-3xl md:text-4xl font-medium tracking-tight text-center"
                data-testid="results-heading"
                style={{ color: colors.text }}
              >
                Plain-English Explanation
              </h2>
              <div className="w-12 h-px" style={{ backgroundColor: colors.accent }} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div 
                data-testid="section-clause-explanation"
                className="md:col-span-2 rounded-xl p-8 md:p-10"
                style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
              >
                <h3 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>
                  Clause Explanation
                </h3>
                <div 
                  className="text-base leading-relaxed whitespace-pre-wrap"
                  style={{ color: colors.textMuted }}
                >
                  {analysis.clause_explanation}
                </div>
              </div>

              <div 
                data-testid="section-why-this-matters"
                className="rounded-xl p-8 md:p-10"
                style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
              >
                <h3 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>
                  Why This Matters
                </h3>
                <div 
                  className="text-base leading-relaxed whitespace-pre-wrap"
                  style={{ color: colors.textMuted }}
                >
                  {analysis.why_this_matters}
                </div>
              </div>

              <div 
                data-testid="section-common-purpose"
                className="rounded-xl p-8 md:p-10"
                style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
              >
                <h3 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>
                  Common Purpose
                </h3>
                <div 
                  className="text-base leading-relaxed whitespace-pre-wrap"
                  style={{ color: colors.textMuted }}
                >
                  {analysis.common_purpose}
                </div>
              </div>

              <div 
                data-testid="section-what-this-clause-can-change"
                className="md:col-span-2 rounded-xl p-8 md:p-10"
                style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
              >
                <h3 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>
                  What This Clause Can Change
                </h3>
                <div 
                  className="text-base leading-relaxed whitespace-pre-wrap"
                  style={{ color: colors.textMuted }}
                >
                  {analysis.what_this_clause_can_change}
                </div>
              </div>

              <div 
                data-testid="section-language-to-notice"
                className="md:col-span-2 rounded-xl p-8 md:p-10"
                style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
              >
                <h3 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>
                  Language to Notice
                </h3>
                <div 
                  className="text-base leading-relaxed whitespace-pre-wrap"
                  style={{ color: colors.textMuted }}
                >
                  {analysis.language_to_notice}
                </div>
              </div>

              <div 
                data-testid="section-important-context"
                className="rounded-xl p-8 md:p-10"
                style={{ 
                  backgroundColor: colors.info, 
                  border: `1px solid ${colors.infoBorder}` 
                }}
              >
                <h3 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>
                  Important Context
                </h3>
                <div 
                  className="text-base leading-relaxed whitespace-pre-wrap"
                  style={{ color: colors.text }}
                >
                  {analysis.important_context}
                </div>
              </div>
            </div>

            <div className="mt-12 text-center">
              <button
                data-testid="analyze-another-button"
                onClick={() => {
                  setAnalysis(null);
                  setClauseText("");
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="text-base rounded-lg px-8 py-4 transition-all"
                style={{ 
                  border: `1px solid ${colors.border}`, 
                  color: colors.textMuted, 
                  backgroundColor: 'transparent' 
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.backgroundLight;
                  e.currentTarget.style.borderColor = colors.borderLight;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = colors.border;
                }}
              >
                Analyze another clause
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer 
        className="px-6 md:px-12 lg:px-24 py-16 text-center"
        style={{ 
          backgroundColor: colors.backgroundDark,
          borderTop: `1px solid ${colors.border}`,
        }}
      >
        <div className="max-w-2xl mx-auto">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img 
              src="/clauseclear-logo.png" 
              alt="ClauseClear" 
              className="h-16 object-contain opacity-80"
            />
          </div>
          
          <p 
            className="text-sm mb-3 leading-relaxed"
            style={{ color: colors.textDim }}
          >
            ClauseClear is an educational tool. It does not provide legal advice or replace a qualified lawyer.
          </p>
          <p className="text-xs mb-6" style={{ color: colors.textDim }}>
            Build v1.4
          </p>
          
          {/* Navigation Links */}
          <div className="flex justify-center gap-8 text-sm mb-6">
            <Link
              to="/"
              className="transition-opacity hover:opacity-80"
              style={{ color: colors.accent }}
            >
              Try the tool
            </Link>
            <Link
              to="/clauses"
              className="transition-opacity hover:opacity-80"
              style={{ color: colors.accent }}
            >
              Browse clauses
            </Link>
            <a
              href="https://buymeacoffee.com/jamesmoneyz"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-opacity hover:opacity-80"
              style={{ color: colors.accent }}
            >
              Support the project
            </a>
          </div>
          
          {/* Copyright */}
          <p className="text-xs" style={{ color: colors.textDim }}>
            © {new Date().getFullYear()} ClauseClear. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      <ScrollToTop />
    </div>
  );
}
